package com.evoapps.evoshift.ui.adaptive

import androidx.compose.foundation.clickable
import androidx.compose.foundation.shape.CornerSize
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusEventModifierNode
import androidx.compose.ui.focus.FocusState
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.drawOutline
import androidx.compose.ui.graphics.drawscope.ContentDrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.input.InputMode
import androidx.compose.ui.node.CompositionLocalConsumerModifierNode
import androidx.compose.ui.node.DrawModifierNode
import androidx.compose.ui.node.ModifierNodeElement
import androidx.compose.ui.node.currentValueOf
import androidx.compose.ui.node.invalidateDraw
import androidx.compose.ui.platform.LocalInputModeManager
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.LocalOnColours
import com.evoapps.evoshift.widgets.OnColours

/*
 * SH-209: the keyboard focus of a clickable card or row. Until now a focused card got the hover
 * tint (CardTint) and a Material Button row its focus state layer: on the web a click focuses
 * what it clicks, so the grey fill (and the Button's darker frame) stayed after the click and
 * looked broken. Focus now shows as a thin ring in the accent colour, a small gap outside the
 * card, following its rounded shape, and only for keyboard focus (focus-visible: Tab or the arrow
 * keys, InputMode.Keyboard), never after a click or a tap. Hover and press keep the CardTint.
 */

/** The ring's geometry and colour. */
object FocusRing {
    /** The ring's stroke. */
    val Width = 2.dp

    /** The gap between the card's edge and the ring: the surface around the card shows in it. */
    val Gap = 2.dp

    /**
     * The ring's colour on the surface around the card: the accent of that surface (the deep
     * accent on the cream screens and cards, the bright one on the base-coloured dialogs, sheets
     * and panes). The ring sits in that surface (the [Gap] keeps it off the card), so it is the
     * only colour it must stand out from: ThemeContrastTest checks at least 3:1 (WCAG 1.4.11,
     * 2.4.13) on every surface in all nine SH-196 combinations.
     */
    fun colorOn(surface: OnColours): Color = surface.accent
}

/**
 * The keyboard [FocusRing] of the clickable that follows (a `clickable`, `focusable`, `toggleable`
 * further down the chain): drawn around [shape] when that is focused and the window is in keyboard
 * input mode. Put it before any `clip` or clipping `shadow`, so the ring can sit outside the card.
 * [inset] draws it the same distance inside the bounds instead: for a row inside a card, whose clip
 * would cut a ring outside the row off (the card's surface is then the one around the ring).
 */
fun Modifier.focusRing(shape: Shape, inset: Boolean = false): Modifier = this then FocusRingElement(shape, inset)

/**
 * A clickable row inside a card or a list (the More screen's entries): the hand cursor, the
 * [CardTint] on hover and press and the keyboard [focusRing] just inside the row (SH-209).
 */
fun Modifier.rowClickable(onClick: () -> Unit): Modifier =
    focusRing(RectangleShape, inset = true)
        .handCursor()
        .clickable(interactionSource = null, indication = CardTint(), onClick = onClick)

private data class FocusRingElement(val shape: Shape, val inset: Boolean) : ModifierNodeElement<FocusRingNode>() {
    override fun create() = FocusRingNode(shape, inset)
    override fun update(node: FocusRingNode) {
        node.shape = shape
        node.inset = inset
        node.invalidateDraw()
    }
}

private class FocusRingNode(var shape: Shape, var inset: Boolean) : Modifier.Node(), DrawModifierNode, FocusEventModifierNode,
    CompositionLocalConsumerModifierNode {
    private var focused = false

    override fun onFocusEvent(focusState: FocusState) {
        if (focused != focusState.isFocused) {
            focused = focusState.isFocused
            invalidateDraw()
        }
    }

    override fun ContentDrawScope.draw() {
        drawContent()
        // Read while drawing: a click (touch mode) hides the ring of what is still focused, Tab shows it again.
        if (!focused || currentValueOf(LocalInputModeManager).inputMode != InputMode.Keyboard) return
        val stroke = FocusRing.Width.toPx()
        // How far the stroke's middle lies outside the bounds (negative: inside).
        val out = (FocusRing.Gap.toPx() + stroke / 2) * (if (inset) -1 else 1)
        val ringSize = Size(size.width + 2 * out, size.height + 2 * out)
        if (ringSize.minDimension <= 0f) return
        val ringShape = (shape as? RoundedCornerShape)?.let { rounded ->
            // Concentric corners: each radius grows (shrinks) by the distance from the card's edge.
            fun CornerSize.grown() = CornerSize((toPx(size, this@draw) + out).coerceAtLeast(0f))
            RoundedCornerShape(rounded.topStart.grown(), rounded.topEnd.grown(), rounded.bottomEnd.grown(), rounded.bottomStart.grown())
        } ?: shape
        val outline = ringShape.createOutline(ringSize, layoutDirection, this)
        translate(-out, -out) {
            drawOutline(outline, FocusRing.colorOn(currentValueOf(LocalOnColours)), style = Stroke(stroke))
        }
    }
}
